package fr.labodoc.healthProfessional.api.client

import arrow.core.left
import arrow.core.right
import fr.labodoc.domain.labodoc.InputFile
import fr.labodoc.healthProfessional.api.ApiResponse
import fr.labodoc.healthProfessional.api.payloads.requests.ChangePasswordRequest
import fr.labodoc.healthProfessional.api.payloads.requests.ContactRequest
import fr.labodoc.healthProfessional.api.payloads.requests.FinalizePasswordResetRequest
import fr.labodoc.healthProfessional.api.payloads.requests.FreestandingApplicationRequest
import fr.labodoc.healthProfessional.api.payloads.requests.LoginRequest
import fr.labodoc.healthProfessional.api.payloads.requests.MedicalCardCPFRegistrationRequest
import fr.labodoc.healthProfessional.api.payloads.requests.MedicalCardCPSRegistrationRequest
import fr.labodoc.healthProfessional.api.payloads.requests.PasswordResetRequest
import fr.labodoc.healthProfessional.api.payloads.requests.RegisterSelfFcmRegistrationTokenRequest
import fr.labodoc.healthProfessional.api.payloads.requests.UpdateSelfMedicalInterestsRequest
import fr.labodoc.healthProfessional.api.payloads.requests.UpdateSelfNotificationsSettingsRequest
import fr.labodoc.healthProfessional.api.payloads.responses.ApplicationInformationResponse
import fr.labodoc.healthProfessional.api.payloads.responses.DepartmentsResponse
import fr.labodoc.healthProfessional.api.payloads.responses.ErrorResponse
import fr.labodoc.healthProfessional.api.payloads.responses.LaboratoriesResponse
import fr.labodoc.healthProfessional.api.payloads.responses.LearnedSocietiesResponse
import fr.labodoc.healthProfessional.api.payloads.responses.MarketingDocumentResponse
import fr.labodoc.healthProfessional.api.payloads.responses.MarketingDocumentsResponse
import fr.labodoc.healthProfessional.api.payloads.responses.MedicalDiplomasResponse
import fr.labodoc.healthProfessional.api.payloads.responses.MedicalProfessionResponse
import fr.labodoc.healthProfessional.api.payloads.responses.MedicalProfessionsResponse
import fr.labodoc.healthProfessional.api.payloads.responses.MedicineWithInformationResponse
import fr.labodoc.healthProfessional.api.payloads.responses.MedicinesSummaryResponse
import fr.labodoc.healthProfessional.api.payloads.responses.MessageResponse
import fr.labodoc.healthProfessional.api.payloads.responses.MessagesSummaryResponse
import fr.labodoc.healthProfessional.api.payloads.responses.SelfResponse
import fr.labodoc.healthProfessional.api.payloads.responses.UniversityHospitalsResponse
import fr.labodoc.healthProfessional.api.resources.Applications
import fr.labodoc.healthProfessional.api.resources.Contact
import fr.labodoc.healthProfessional.api.resources.Departments
import fr.labodoc.healthProfessional.api.resources.Laboratories
import fr.labodoc.healthProfessional.api.resources.LearnedSocieties
import fr.labodoc.healthProfessional.api.resources.MedicalDiplomas
import fr.labodoc.healthProfessional.api.resources.MedicalProfessions
import fr.labodoc.healthProfessional.api.resources.Medicines
import fr.labodoc.healthProfessional.api.resources.Messages
import fr.labodoc.healthProfessional.api.resources.UniversityHospitals
import fr.labodoc.healthProfessional.api.resources.Users
import fr.labodoc.healthProfessional.api.routes.ApplicationsRoutes
import fr.labodoc.healthProfessional.api.routes.ContactRoutes
import fr.labodoc.healthProfessional.api.routes.DepartmentsRoutes
import fr.labodoc.healthProfessional.api.routes.LaboratoriesRoutes
import fr.labodoc.healthProfessional.api.routes.LearnedSocietiesRoutes
import fr.labodoc.healthProfessional.api.routes.MedicalDiplomasRoutes
import fr.labodoc.healthProfessional.api.routes.MedicalProfessionsRoutes
import fr.labodoc.healthProfessional.api.routes.MedicinesRoutes
import fr.labodoc.healthProfessional.api.routes.MessagesRoutes
import fr.labodoc.healthProfessional.api.routes.UniversityHospitalRoutes
import fr.labodoc.healthProfessional.api.routes.UsersRoutes
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.resources.delete
import io.ktor.client.plugins.resources.get
import io.ktor.client.plugins.resources.post
import io.ktor.client.plugins.resources.put
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.json.Json

class ApiClient(
  private val httpClient: HttpClient
) : ApplicationsRoutes, ContactRoutes, DepartmentsRoutes, LaboratoriesRoutes, LearnedSocietiesRoutes,
    MedicalDiplomasRoutes, MedicalProfessionsRoutes, MedicinesRoutes, MessagesRoutes, UniversityHospitalRoutes,
    UsersRoutes {
  override suspend fun getHealthProfessionalInformationApplicationByCardNumber(
    medicalCardNumber: String
  ): ApiResponse<ApplicationInformationResponse> {
    val response = httpClient.get(Applications.WithMedicalCard.Number(medicalCardNumber = medicalCardNumber))

    return if (response.status.isSuccess())
      response.body<ApplicationInformationResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun registerWithMedicalCardCPS(
      payload: MedicalCardCPSRegistrationRequest,
  ): ApiResponse<Unit> {
    val response = httpClient.post(Applications.WithMedicalCard.CPS()) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun registerWithMedicalCardCPF(
    payload: MedicalCardCPFRegistrationRequest
  ): ApiResponse<Unit> {
    val response = httpClient.post(Applications.WithMedicalCard.CPF()) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun registerWithoutMedicalCard(
      payload: FreestandingApplicationRequest,
      supportingDocument: InputFile
  ): ApiResponse<Unit> {
    val response = httpClient.post(Applications.WithoutMedicalCard()) {
      setBody(
          MultiPartFormDataContent(
              formData {
                  append("payload", Json.Default.encodeToString(payload), Headers.Companion.build {
                      append(HttpHeaders.ContentType, "application/json")
                  })
                  append("supporting-document", supportingDocument.content, Headers.Companion.build {
                      append(HttpHeaders.ContentType, supportingDocument.mime)
                      append(HttpHeaders.ContentDisposition, "filename=\"${supportingDocument.name}\"")
                  })
              }
          ))
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun validateApplicationEmail(
    token: String
  ): ApiResponse<Unit> {
    val response = httpClient.post(Applications.ValidateEmail(token))

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun sendContactRequest(payload: ContactRequest): ApiResponse<Unit> {
    val response = httpClient.post(Contact()) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getDepartments(
  ): ApiResponse<DepartmentsResponse> {
    val response = httpClient.get(Departments())

    return if (response.status.isSuccess())
      response.body<DepartmentsResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getLaboratories(
  ): ApiResponse<LaboratoriesResponse> {
    val response = httpClient.get(Laboratories())

    return if (response.status.isSuccess())
      response.body<LaboratoriesResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun favoriteLaboratory(
    id: String
  ): ApiResponse<Unit> {
    val response = httpClient.post(Laboratories.Laboratory.Favorite(id))

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun unfavoriteLaboratory(
    id: String
  ): ApiResponse<Unit> {
    val response = httpClient.delete(Laboratories.Laboratory.Favorite(id))

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getLearnedSocieties(
  ): ApiResponse<LearnedSocietiesResponse> {
    val response = httpClient.get(LearnedSocieties())

    return if (response.status.isSuccess())
      response.body<LearnedSocietiesResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun favoriteLearnedSociety(
    id: String
  ): ApiResponse<Unit> {
    val response = httpClient.post(LearnedSocieties.LearnedSociety.Favorite(id))

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun unfavoriteLearnedSociety(
    id: String
  ): ApiResponse<Unit> {
    val response = httpClient.delete(LearnedSocieties.LearnedSociety.Favorite(id))

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMedicalDiplomas(
    forHealthDirectoryProfessionCode: String?
  ): ApiResponse<MedicalDiplomasResponse> {
    val response = httpClient.get(MedicalDiplomas(forHealthDirectoryProfessionCode))

    return if (response.status.isSuccess())
      response.body<MedicalDiplomasResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMedicalProfessions(
  ): ApiResponse<MedicalProfessionsResponse> {
    val response = httpClient.get(MedicalProfessions())

    return if (response.status.isSuccess())
      response.body<MedicalProfessionsResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMedicalProfession(
    id: String
  ): ApiResponse<MedicalProfessionResponse> {
    val response = httpClient.get(MedicalProfessions.MedicalProfession(id))

    return if (response.status.isSuccess())
      response.body<MedicalProfessionResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMedicines(
  ): ApiResponse<MedicinesSummaryResponse> {
    val response = httpClient.get(Medicines())

    return if (response.status.isSuccess())
      response.body<MedicinesSummaryResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMedicine(
    id: String
  ): ApiResponse<MedicineWithInformationResponse> {
    val response = httpClient.get(Medicines.Medicine(id))

    return if (response.status.isSuccess())
      response.body<MedicineWithInformationResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMarketingDocuments(
    medicineId: String
  ): ApiResponse<MarketingDocumentsResponse> {
    val response = httpClient.get(Medicines.Medicine.MarketingDocuments(medicineId))

    return if (response.status.isSuccess())
      response.body<MarketingDocumentsResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMarketingDocument(
    id: String,
    medicineId: String
  ): ApiResponse<MarketingDocumentResponse> {
    val response = httpClient.get(Medicines.Medicine.MarketingDocuments.MarketingDocument(medicineId, id))

    return if (response.status.isSuccess())
      response.body<MarketingDocumentResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMedicineMessages(
    id: String
  ): ApiResponse<MessagesSummaryResponse> {
    val response = httpClient.get(Medicines.Medicine.Messages(id))

    return if (response.status.isSuccess())
      response.body<MessagesSummaryResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMessages(
    favorite: Boolean,
    flashInfo: Boolean,
    invitation: Boolean
  ): ApiResponse<MessagesSummaryResponse> {
    val response = httpClient.get(Messages(onlyFavorite = favorite, flashInfo = flashInfo, invitation = invitation))

    return if (response.status.isSuccess())
      response.body<MessagesSummaryResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getMessage(
    id: String
  ): ApiResponse<MessageResponse> {
    val response = httpClient.get(Messages.Message(id))

    return if (response.status.isSuccess())
      response.body<MessageResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun favoriteMessage(
    id: String
  ): ApiResponse<Unit> {
    val response = httpClient.post(Messages.Message.Favorite(id))

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun unfavoriteMessage(
    id: String
  ): ApiResponse<Unit> {
    val response = httpClient.delete(Messages.Message.Favorite(id))

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getUniversityHospitals(): ApiResponse<UniversityHospitalsResponse> {
    val response = httpClient.get(UniversityHospitals())

    return if (response.status.isSuccess())
      response.body<UniversityHospitalsResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun login(
    payload: LoginRequest
  ): ApiResponse<SelfResponse> {
    val response = httpClient.post(Users.Login()) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      response.body<SelfResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun logout(
  ): ApiResponse<Unit> {
    val response = httpClient.post(Users.Self.Logout())

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun changePassword(
    payload: ChangePasswordRequest
  ): ApiResponse<Unit> {
    val response = httpClient.put(Users.Self.Password()) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun sendPasswordResetRequest(
    payload: PasswordResetRequest
  ): ApiResponse<Unit> {
    val response = httpClient.post(Users.Password.Reset()) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun finalizePasswordResetRequest(
    token: String,
    payload: FinalizePasswordResetRequest
  ): ApiResponse<Unit> {
    val response = httpClient.post(Users.Password.Reset.Validate(token)) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun getSelf(
  ): ApiResponse<SelfResponse> {
    val response = httpClient.get(Users.Self())

    return if (response.status.isSuccess())
      response.body<SelfResponse>().right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun registerFcmRegistrationToken(
    payload: RegisterSelfFcmRegistrationTokenRequest
  ): ApiResponse<Unit> {
    val response = httpClient.post(Users.Self.Fcm()) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun deleteFcmRegistrationToken(
    token: String
  ): ApiResponse<Unit> {
    val response = httpClient.delete(Users.Self.Fcm.DeviceToken(token))

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun updateMedicalInterests(
    payload: UpdateSelfMedicalInterestsRequest
  ): ApiResponse<Unit> {
    val response = httpClient.put(Users.Self.MedicalInterests()) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun updateNotificationsSettings(
    payload: UpdateSelfNotificationsSettingsRequest
  ): ApiResponse<Unit> {
    val response = httpClient.put(Users.Self.NotificationSettings()) {
      contentType(ContentType.Application.Json)
      setBody(payload)
    }

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }

  override suspend fun deleteAccount(): ApiResponse<Unit> {
    val response = httpClient.delete(Users.Self())

    return if (response.status.isSuccess())
      Unit.right()
    else
      response.body<ErrorResponse>().left()
  }
}
